import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTree } from '../../hooks/useTree';
import { ConvertListOfListToDataConnectionRichTreeViewList } from '../CommonFunctions/CommonFunctions';
import RichTreeViewControl from './RichTree';
import { Box, Typography } from '@mui/material';
import { ffButtonStyle } from '../Controls/muiStyles';
import { fontSize, palette } from '../..';
import HoverButton from '../Buttons/HoverButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';

interface TreeViewProps {
  type: 'db' | 'ff' | 'wa';
  setFunction: Dispatch<SetStateAction<'ff' | 'db' | 'wa' | null>>;
  proceedFunction: (selected: string, list: any[], checked: boolean) => void;
  currentWorkspace: string;
  autoOpenCurrentWorkspace?: boolean;
}
function TreeView({
  type,
  setFunction,
  proceedFunction,
  currentWorkspace,
  autoOpenCurrentWorkspace,
}: TreeViewProps) {
  const { path, selectedFlatFiles } = useSelector((state: RootState) => {
    const selected = state.dataset.selectedConnectionId;
    let path: string[] = [];
    const connection = state.dataset.connections.find((conn) => conn.id === selected);
    if (connection && connection.workspaces) {
      path = connection.workspaces;
    }
    return {
      path: JSON.stringify(path),
      selectedFlatFiles: state.dataset.connections
        .filter((conn) => conn.fileType === 'flatfile')
        .map((ff) => ff.id),
    };
  });
  const [treeList, setTreeList] = useState<any>([]);
  const [lastSelectedDB, setLastSelectedDB] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const { result, loading } = useTree(type);
  const currentWorkSpaceRef = useRef(false);

  useEffect(() => {
    setTreeList(
      ConvertListOfListToDataConnectionRichTreeViewList(
        result,
        type === 'ff' ? 'flatfile' : type === 'wa' ? 'webapi' : 'dbcon',
      ),
    );
  }, [result]);
  const expandCurrentWorkspace = (delay?: number) => {
    let expandedIds: string[] = [];

    const workspace = treeList.find((item: any) => item.id === currentWorkspace);

    if (workspace) {
      expandedIds = [currentWorkspace];
    } else {
      const parent = treeList.find((item: any) =>
        item.children?.some((child: any) => child.id === currentWorkspace),
      );

      expandedIds = [parent?.id ?? '', currentWorkspace];
    }

    const expand = () => {
      setExpanded(expandedIds);
      currentWorkSpaceRef.current = true;
      setTimeout(() => {
        currentWorkSpaceRef.current = false;
      }, 1000);
    };

    delay !== undefined ? setTimeout(expand, delay) : expand();
  };

  useEffect(() => {
    if (treeList.length === 0 || !autoOpenCurrentWorkspace) return;

    expandCurrentWorkspace(0);
  }, [autoOpenCurrentWorkspace, treeList, currentWorkspace]);

  function handleCancel() {
    setFunction(null);
  }
  function handleSelection(id: string) {
    if (type === 'db' || type === 'wa') {
      setLastSelectedDB(id);
    }
  }

  // useEffect(() => {
  //   if (type === 'ff' && selectedFlatfiles.length > 0) {
  //     proceedFunction(selectedFlatfiles, treeList);
  //   }
  // }, [selectedFlatfiles]);

  return (
    <Box
      sx={{
        maxWidth: '29rem',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Typography
        sx={{
          paddingBlock: '0.5rem',
          fontWeight: 'bold',
          color: '#484848', //484848
          background: '#80808045',
          mt: 1,
        }}
      >
        {type === 'db'
          ? 'Select DB Connection'
          : type === 'wa'
            ? 'Select Web API Connection'
            : 'Select Flatfile'}
      </Typography>
      <div
        style={{
          flexGrow: 1,
          overflowY: 'auto',
          height: 'calc(100% - 5rem)',
          position: 'relative',
        }}
      >
        {loading ? (
          <div className="spinner-overlay">
            <div className="spinner-2"></div>
          </div>
        ) : (
          <RichTreeViewControl
            list={treeList}
            handleDirectClick={handleSelection}
            currentWorkspace={currentWorkspace}
            fileType={type === 'ff' ? 'flatfile' : type === 'wa' ? 'webapi' : 'dbcon'}
            handleProceedButtonClick={proceedFunction}
            itemClickedIncanvas={path}
            selectedFlatFiles={selectedFlatFiles}
            expanded={expanded}
            setExpanded={setExpanded}
            currentWorkSpaceRef={currentWorkSpaceRef.current}
          />
        )}
      </div>
      <div
        style={{
          padding: '1rem 0 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          columnGap: '1rem',
          marginBottom: '0.45rem',
        }}
      >
        <HoverButton
          text="Current Workspace"
          color="secondary.light"
          hoverColor="secondary.contrastText"
          backgroundColor="secondary.contastText"
          hoverBackgroundColor="secondary.light"
          sx={{
            ...ffButtonStyle,
            border: `1px solid ${palette.secondary.light}`,
            fontSize: fontSize.medium,
            lineHeight: 'normal',
            height: '24px',
          }}
          onClick={expandCurrentWorkspace}
          transitionTime="0.2s"
        />

        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <HoverButton
            sx={{
              ...ffButtonStyle,
              border: `1px solid ${palette.primary.contrastText}`,
              fontSize: fontSize.medium,
              lineHeight: 'normal',
              height: '24px',
            }}
            onClick={handleCancel}
            text="Close"
            hoverColor="secondary.contrastText"
            color="primary.contrastText"
            hoverBackgroundColor="primary.contrastText"
            backgroundColor="secondary.contrastText"
            transitionTime="0.2s"
          />
          {(type === 'db' || type === 'wa') && lastSelectedDB && (
            <HoverButton
              onClick={() => proceedFunction(lastSelectedDB, treeList, true)}
              text="Add"
              color="primary.main"
              hoverColor="secondary.contrastText"
              backgroundColor="secondary.contrastText"
              hoverBackgroundColor="primary.main"
              transitionTime="0.2s"
              sx={{
                ...ffButtonStyle,
                border: `1px solid ${palette.primary.main}`,
                fontSize: fontSize.medium,
                lineHeight: 'normal',
                height: '24px',
              }}
            />
          )}
        </Box>
      </div>
    </Box>
  );
}

export default TreeView;
